/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { fetchData, postData, deleteData, patchData, putData } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import { IIDPData, IUsers, IUserRequest, INativeUserRequest, UserSearch, IUserDetails, INativeUserUpdateRequest, IUserPasswordResetResponse, IFedratedUserUpdateRequest } from '../models/Users/UserContracts';
import { convertObjectToQueryString, createRequestObject, getAPIMUrl } from '../utility/commonUtility';
import { NATIVE_STORE_LABEL } from '../utility/constants';
import { IRole } from "../models/Roles/RolesContracts";
import {NATIVE_CAMEL_CASE, NATIVE_UPPER_CASE, LOCAL_CAMEL_CASE} from '../container/Settings/constants';
const { CLIENT_SERVICE_V1_URL, ENTITLEMENT_SERVICE, USER_MACHINE_SERVICE, CLIENT_SERVICE_V2_URL, IDENTITY_SERVICE } = window['env'];

export const getRealmServiceUsers = async (queryParams?: UserSearch, locationName?: string) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<IUsers>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getSubjectServiceUsers = async (queryParams?: UserSearch, locationName?: string) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<IUsers>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const updateFedaratedUserDetails = async (idpSubId: string, data: IFedratedUserUpdateRequest) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${idpSubId}`;

  return patchData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getAssignedUsers = async (id: number, queryParams: any, locationName?: string) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${id}/users`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<any>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => appsResponse)
    .catch((error: any) => {
      throw error;
    });
};

export const getLocationsAssignedToUser = async (id: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${id}/locations`;
  return fetchData<IRole[]>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getUserDetails = async (userId: string, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${userId}`;
  return fetchData<IUserDetails>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const createNewUser = async (data: IUserRequest | INativeUserRequest, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users`;
  return postData<any>(createRequestObject(apiURL), data, locationName ? {
    headers: {
      'LOCATION-CONTEXT': locationName
    }
  } : getAuthToken).then((appsResponse) => {
    return appsResponse;
  }).catch((error: any) => {
    throw error;
  });
};

export const updateNativeUserDetails = async (userId: string, data: INativeUserUpdateRequest) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${userId}`;

  return patchData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const resetUserPassword = async (userId: string, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${userId}/resetPassword`;
  return postData<IUserPasswordResetResponse>(
    createRequestObject(apiURL),
    null,
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const resetUserMFA = async (idOrIdpSubjectId: string | number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${idOrIdpSubjectId}/resetMfa`;
  return deleteData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const resendInvite = async (idOrIdpSubjectId: string | number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${idOrIdpSubjectId}/resendRegistrationConfirmation`;
  return postData<any>(createRequestObject(apiURL), null, getAuthToken)
      .then(appsResponse => {
          return appsResponse;
      })
      .catch((error: any) => {
          throw error;
      });
};

export const getEntitlementServiceIdps = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE}/realmIdps`;
  return fetchData<IIDPData>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      const updatedIDPList = appsResponse.idps?.map((idp: any) => (idp.idpType === NATIVE_CAMEL_CASE || idp.idpType === LOCAL_CAMEL_CASE) ? { ...idp, name: NATIVE_STORE_LABEL } : idp)
      return { idps: updatedIDPList };
    })
    .catch((error: any) => {
      throw error;
    });
}
export const getIdentityServiceIdps = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${IDENTITY_SERVICE}/idp`;
  return fetchData<IIDPData>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      const updatedIDPList = appsResponse.data?.map((idp: any) => (idp.type === NATIVE_UPPER_CASE) ? { ...idp, name: NATIVE_STORE_LABEL } : idp)
      return { idps: updatedIDPList };
    })
    .catch((error: any) => {
      throw error;
    });
}
export const deleteUser = async (userId: string, forceDelete: boolean = false) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/users/${userId}?deleteUserby=id`;
  apiURL = forceDelete ? `${apiURL}&forceDelete=true` : apiURL;
  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getRolesAssignedToUser = async (id: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/subjects/${id}/roles`;

  return fetchData<IRole[]>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getRolesByGroupAssignedToUser = async (id: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V2_URL}/subjects/${id}/roles`;

  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const getGraviteeUsers = async (queryParams?: UserSearch, locationName?: string) => {
  let queryData = '';
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users`;

  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<IUsers>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};
/* Directory APIs */
export const getDirectoryUserDetails = async (userId: string, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${userId}`;
  return fetchData<IUserDetails>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const createNewDirectoryUser = async (data: any, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiUrl = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users`;
  return postData<any>(createRequestObject(apiUrl), data, locationName
    ? {
      headers: {
        'LOCATION-CONTEXT': locationName
      }
    }
    : getAuthToken
  );
};

export const updateDirectoryUserDetails = async (idOrIdpSubId: string, data: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiUrl = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${idOrIdpSubId}`;
  return patchData<any>(createRequestObject(apiUrl), data, getAuthToken);
};

export const deleteDirectoryUser = async (userid: string, forceDelete: boolean) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${userid}?deleteUserby=id`;
  apiURL = forceDelete ? `${apiURL}&forceDelete=true` : apiURL;
  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
}

export const signInBlockEnable = async (idOrIdpSubId: string, enabled: any, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiUrl = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${idOrIdpSubId}/enabled`;
  return putData<any>(
    createRequestObject(apiUrl),
    enabled,
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  );
};

export const resetUserDirectoryPassword = async (params: any, locationName?: string) => {
  const payload = { password: params.password, forceResetPassword: params.forceResetPassword };
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${params.userId}/resetPassword`;
  return postData<IUserPasswordResetResponse>(
    createRequestObject(apiURL),
    payload,
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getLocationsAssociatedToUser = async (userId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${userId}/locations`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const associateDisassociateUserToLocation = async (userId: any, data: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${USER_MACHINE_SERVICE}/users/${userId}/locations`;

  return patchData(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getRealmById = async (realmId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${ENTITLEMENT_SERVICE}/realms/${realmId}`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

